/**
 * ReservationCard Component
 *
 * @description 個別予約カードコンポーネント
 * 世界最高水準のマイクロインタラクションとアクセシビリティを実装
 *
 * Features:
 * - React.memo最適化による再レンダリング防止
 * - Framer Motionによる洗練されたホバーアニメーション
 * - WCAG 2.1 AA準拠のアクセシビリティ
 * - ステータス別の視覚的フィードバック
 * - VIP顧客の強調表示
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Gem, XCircle, User } from 'lucide-react';
import type { ReservationCardProps } from '@/types/dashboard';

/**
 * ReservationCard Component
 */
const ReservationCard: React.FC<ReservationCardProps> = ({ reservation, onClick }) => {
  const isCancelled = reservation.status === 'cancelled';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={!isCancelled ? { y: -2, transition: { duration: 0.2 } } : undefined}
      transition={{ duration: 0.3 }}
    >
      <Card
        className={`
          p-4 transition-all
          ${isCancelled ? 'opacity-60' : 'cursor-pointer hover:bg-muted/30 hover:shadow-md'}
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
        `}
        onClick={!isCancelled ? onClick : undefined}
        role={!isCancelled ? 'button' : undefined}
        tabIndex={!isCancelled ? 0 : undefined}
        onKeyDown={(e) => {
          if (!isCancelled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick();
          }
        }}
        aria-label={`${reservation.time} ${reservation.customer}様の予約 ${reservation.service}`}
      >
        <div className="flex gap-4">
          {/* 時刻表示 */}
          <div className="min-w-[60px] text-center pt-1" aria-label={`予約時刻 ${reservation.time}`}>
            <p className="text-2xl font-semibold text-primary leading-none">
              {reservation.time.split(':')[0]}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {reservation.time.split(':')[1]}
            </p>
          </div>

          {/* 予約内容 */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="text-base font-medium truncate">
                {reservation.customer}
              </p>

              {/* 優良顧客バッジ */}
              {reservation.isVip && (
                <Badge
                  variant="secondary"
                  className="h-5 text-xs bg-orange-50 text-orange-700 border-orange-200 whitespace-nowrap"
                >
                  <Gem className="mr-1 h-3 w-3" aria-hidden="true" />
                  優良顧客
                </Badge>
              )}

              {/* キャンセルバッジ */}
              {isCancelled && (
                <Badge variant="outline" className="h-5 text-xs whitespace-nowrap">
                  <XCircle className="mr-1 h-3 w-3" aria-hidden="true" />
                  キャンセル
                </Badge>
              )}
            </div>

            <p className="text-sm text-muted-foreground truncate flex items-center gap-1">
              <span>{reservation.service}</span>
              <span>•</span>
              <User className="h-3.5 w-3.5 inline-block" aria-hidden="true" />
              <span>{reservation.staff}</span>
            </p>

            {!isCancelled && (
              <p className="text-xs text-muted-foreground font-medium mt-1">
                {reservation.visitCount}回目の来店
              </p>
            )}
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

/**
 * React.memoでラップして不要な再レンダリングを防止
 * 予約情報が変更されない限り再レンダリングしない
 */
export default React.memo(ReservationCard, (prevProps, nextProps) => {
  return (
    prevProps.reservation.id === nextProps.reservation.id &&
    prevProps.reservation.status === nextProps.reservation.status &&
    prevProps.reservation.isVip === nextProps.reservation.isVip &&
    prevProps.reservation.visitCount === nextProps.reservation.visitCount
  );
});
